import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MAX_PLAYERS, MIN_PLAYERS, scoringConfigFor } from '@shared/game-logic';
import { Button, Panel, Screen } from '@/components/ui';
import GameSettings, { type GameSettingsValue } from '@/components/GameSettings';
import { seatColor } from '@/lib/labels';
import { useScorekeeper } from '@/store/scorekeeper';
import { useRules } from '@/store/rules';

interface DraftSeat {
  displayName: string;
  profileId: string | null;
}

export default function NewGame() {
  const navigate = useNavigate();
  const profiles = useScorekeeper((state) => state.profiles);
  const createGame = useScorekeeper((state) => state.createGame);
  const addProfile = useScorekeeper((state) => state.addProfile);

  const [seats, setSeats] = useState<DraftSeat[]>([]);
  const [nameDraft, setNameDraft] = useState('');
  // The rules this device last played, at the table or live — one memory.
  const remembered = useRules((state) => state.rules);
  const remember = useRules((state) => state.remember);
  const [settings, setSettings] = useState<GameSettingsValue>(remembered);
  const [firstDealerSeat, setFirstDealerSeat] = useState(0);

  const enoughPlayers = seats.length >= MIN_PLAYERS;
  const unusedProfiles = profiles.filter(
    (profile) => !seats.some((seat) => seat.profileId === profile.id)
  );

  function addSeat(displayName: string, profileId: string | null) {
    const name = displayName.trim();
    if (!name || seats.length >= MAX_PLAYERS) return;
    setSeats((current) => [...current, { displayName: name, profileId }]);
    setNameDraft('');
  }

  function addTypedName() {
    const name = nameDraft.trim();
    if (!name) return;
    // Reuse a saved profile when the name matches, so stats keep accumulating.
    const existing = profiles.find((profile) => profile.name.toLowerCase() === name.toLowerCase());
    const profile = existing ?? addProfile(name);
    addSeat(profile.name, profile.id);
  }

  function removeSeat(index: number) {
    setSeats((current) => current.filter((_, i) => i !== index));
    setFirstDealerSeat((current) => Math.max(0, Math.min(current, seats.length - 2)));
  }

  function moveSeat(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= seats.length) return;
    setSeats((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function start() {
    if (!enoughPlayers) return;
    const id = createGame({
      seats,
      maxCards: settings.maxCards,
      direction: settings.direction,
      repeatTopRound: settings.repeatTopRound,
      extraNoTrumpRound: settings.extraNoTrumpRound,
      noTrumpOnTheWayDown: settings.noTrumpOnTheWayDown,
      firstDealerSeat: Math.min(firstDealerSeat, seats.length - 1),
      scoringPreset: settings.scoringPreset,
      scoring:
        settings.scoringPreset === 'eigen'
          ? settings.customScoring
          : scoringConfigFor(settings.scoringPreset),
      houseRules: settings.houseRules,
    });
    // Only what was actually started, so an abandoned fiddle does not stick.
    remember(settings);
    navigate(`/score/${id}`, { replace: true });
  }

  return (
    <Screen
      title="Nieuw potje boerenbridge"
      back="/"
      footer={
        <Button variant="primary" full onClick={start} disabled={!enoughPlayers}>
          {enoughPlayers
            ? `Start met ${seats.length} spelers`
            : `Nog ${MIN_PLAYERS - seats.length} speler${MIN_PLAYERS - seats.length === 1 ? '' : 's'} nodig`}
        </Button>
      }
    >
      <Panel title={`Spelers (${seats.length}/${MAX_PLAYERS})`}>
        <ul className="mb-3 space-y-2">
          {seats.map((seat, index) => (
            <li key={`${seat.displayName}-${index}`} className="flex items-center gap-2">
              <span
                className="h-3 w-3 shrink-0 rounded-full"
                style={{ backgroundColor: seatColor(index) }}
              />
              <span className="min-w-0 flex-1 truncate">{seat.displayName}</span>
              <button
                type="button"
                aria-label="Naar boven"
                onClick={() => moveSeat(index, -1)}
                disabled={index === 0}
                className="h-9 w-9 rounded-lg bg-base-700 text-slate-300 disabled:opacity-30"
              >
                ↑
              </button>
              <button
                type="button"
                aria-label="Naar beneden"
                onClick={() => moveSeat(index, 1)}
                disabled={index === seats.length - 1}
                className="h-9 w-9 rounded-lg bg-base-700 text-slate-300 disabled:opacity-30"
              >
                ↓
              </button>
              <button
                type="button"
                aria-label={`${seat.displayName} verwijderen`}
                onClick={() => removeSeat(index)}
                className="h-9 w-9 rounded-lg bg-red-500/15 text-red-300"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>

        {seats.length < MAX_PLAYERS && (
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              addTypedName();
            }}
          >
            <input
              value={nameDraft}
              onChange={(event) => setNameDraft(event.target.value)}
              placeholder="Naam"
              autoComplete="off"
              className="min-h-12 min-w-0 flex-1 rounded-xl border border-white/10 bg-base-900 px-3 text-base outline-none focus:border-accent-500"
            />
            <Button type="submit" disabled={!nameDraft.trim()}>
              Erbij
            </Button>
          </form>
        )}

        {unusedProfiles.length > 0 && seats.length < MAX_PLAYERS && (
          <div className="mt-3">
            <p className="mb-2 text-sm text-slate-400">Eerder gespeeld</p>
            <div className="flex flex-wrap gap-2">
              {unusedProfiles.map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  onClick={() => addSeat(profile.name, profile.id)}
                  className="rounded-full bg-base-700 px-3 py-2 text-sm hover:bg-base-600"
                >
                  {profile.emoji} {profile.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="mt-3 text-sm text-slate-500">
          De volgorde is de zitorde aan tafel — dat bepaalt wie na wie biedt en uitkomt.
        </p>
      </Panel>

      {enoughPlayers && (
        <Panel title="Wie deelt de eerste ronde">
          <div className="flex flex-wrap gap-2">
            {seats.map((seat, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setFirstDealerSeat(index)}
                aria-pressed={firstDealerSeat === index}
                className={[
                  'rounded-full px-3 py-2 text-sm',
                  firstDealerSeat === index
                    ? 'bg-accent-500 font-semibold text-base-950'
                    : 'bg-base-700 hover:bg-base-600',
                ].join(' ')}
              >
                {seat.displayName}
              </button>
            ))}
          </div>
          <p className="mt-3 text-sm text-slate-500">
            Het delen schuift daarna elke ronde één plek op.
          </p>
        </Panel>
      )}

      <GameSettings
        value={settings}
        onChange={(patch) => setSettings((current) => ({ ...current, ...patch }))}
        table={enoughPlayers ? { known: true, playerCount: seats.length } : { known: false }}
      />
    </Screen>
  );
}
